import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { ArrowLeft, Loader2, Tv, Calendar, Clock, Tag, Play, Trash2, Sparkles, RefreshCw, Download, FileText, ShieldCheck, Newspaper, ListVideo } from "lucide-react";
import Highlight from "../components/Highlight";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import SummaryReport from "../components/SummaryReport";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const VIDEO_PLACEHOLDER = "https://images.unsplash.com/photo-1742805382149-3c2f0cd0f300?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxuZXdzJTIwYnJvYWRjYXN0JTIwc2NyZWVuJTIwcGxhY2Vob2xkZXJ8ZW58MHx8fHwxNzgwNDQ0NDM3fDA&ixlib=rb-4.1.0&q=85";

// Formats that browsers can play natively in <video>
const PLAYABLE_EXT = [".mp4", ".webm", ".mov", ".m4v"];

function getEffectiveSuffix(filename = "") {
  const parts = filename.toLowerCase().split(".");
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1];
    if (/^\d+$/.test(lastPart) && parts.length >= 3) {
      return "." + parts[parts.length - 2];
    }
    return "." + lastPart;
  }
  return "";
}

function isBrowserPlayable(filename = "") {
  const ext = getEffectiveSuffix(filename);
  return PLAYABLE_EXT.includes(ext);
}

function fmtTime(s) {
  if (!s && s !== 0) return "—";
  const t = Math.floor(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const t = Math.floor(seconds);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  return h > 0 ? `${h}j ${m}m` : `${m}m`;
}

export default function TranscriptDetailPage() {
  const { videoId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [activeSegment, setActiveSegment] = useState(-1);
  const [summary, setSummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [segmenting, setSegmenting] = useState(false);
  const [activeNewsId, setActiveNewsId] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    api
      .get(`/transcripts/by-video/${videoId}`)
      .then((r) => {
        setData(r.data);
        setSummary(r.data.summary || null);
        setNewsItems(r.data.news_items || []);
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [videoId]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/videos/${videoId}`);
      toast.success("Rekaman & transkrip dihapus");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  const generateSummary = async () => {
    if (!data?.transcript?.id) return;
    setGeneratingSummary(true);
    try {
      const { data: newSum } = await api.post(`/transcripts/${data.transcript.id}/summary`);
      setSummary(newSum);
      toast.success("Ringkasan intelijen berhasil dibuat");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGeneratingSummary(false);
    }
  };

  const downloadSummaryTxt = () => {
    if (!summary?.content) return;
    const fileName = (data?.video?.file_name || "transkrip")
      .replace(/\.[^.]+$/, "")
      .replace(/[^A-Za-z0-9_.-]+/g, "_");
    const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
    const blob = new Blob([summary.content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}_${ts}_Ringkasan_Militer.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Ringkasan diunduh");
  };

  const regenerateNews = async () => {
    if (!data?.transcript?.id) return;
    setSegmenting(true);
    try {
      const res = await api.post(`/transcripts/${data.transcript.id}/segment-news`);
      // Refetch news items
      const { data: latest } = await api.get(`/videos/${videoId}/news`);
      setNewsItems(latest);
      toast.success(`Berhasil dipisah jadi ${res.data.count} berita`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSegmenting(false);
    }
  };

  const seekTo = (seconds) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = Math.max(0, seconds - 0.2);
      v.play().catch(() => {}); // autoplay rejected by browser policy — non-fatal
    } catch (e) {
      console.debug("seekTo unsupported:", e);
    }
  };

  // Auto-seek to a news item when ?news=<id> is in URL
  useEffect(() => {
    const target = searchParams.get("news");
    if (!target || !newsItems.length) return;
    const item = newsItems.find((n) => n.id === target);
    if (!item) return;
    // Wait for video element to be ready
    const v = videoRef.current;
    if (!v) return;
    const trySeek = () => {
      if (v.readyState >= 1) {
        v.currentTime = Math.max(0, item.start_time - 0.2);
        v.play().catch(() => {});
        setActiveNewsId(item.id);
      } else {
        v.addEventListener("loadedmetadata", trySeek, { once: true });
      }
    };
    trySeek();
  }, [searchParams, newsItems]);

  // Track which segment + news is currently playing
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const segs = data?.transcript?.segments || [];
    const news = newsItems || [];
    const onTime = () => {
      const t = v.currentTime;
      if (segs.length) {
        const idx = segs.findIndex((s) => t >= s.start && t < s.end);
        setActiveSegment(idx);
      }
      const activeN = news.find((n) => t >= n.start_time && t < n.end_time);
      setActiveNewsId(activeN ? activeN.id : null);
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [data, newsItems]);

  if (loading) {
    return (
      <div className="px-6 py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-6 py-10 max-w-3xl mx-auto">
        <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-900 underline inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
        <div className="mt-6 border border-red-200 bg-red-50 text-red-800 rounded-sm p-4 text-sm" data-testid="detail-error">
          {error || "Data tidak tersedia"}
        </div>
      </div>
    );
  }

  const { transcript, video, keyword_hits } = data;
  const allKeywords = (keyword_hits || []).map((h) => h.keyword);

  return (
    <div className="px-6 py-6 space-y-5" data-testid="transcript-detail-page">
      <Link to="/search" className="text-sm text-slate-600 hover:text-slate-900 underline inline-flex items-center gap-1" data-testid="back-link">
        <ArrowLeft className="h-4 w-4" /> Kembali ke pencarian
      </Link>

      <header className="border-b border-slate-200 pb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Detail Transkrip</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-1 truncate" data-testid="detail-title">{video?.file_name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><Tv className="h-3 w-3" /> {video?.channel_name || "—"}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {video?.recording_date || "—"}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDuration(video?.duration)}</span>
            <span className="font-mono">Engine: {transcript.ai_engine}</span>
          </div>
        </div>
        {user?.role === "admin" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600"
                data-testid="detail-delete-button"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Hapus Rekaman
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm" data-testid="detail-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus rekaman & transkrip ini?</AlertDialogTitle>
                <AlertDialogDescription className="text-sm text-slate-600">
                  <span className="block font-medium text-slate-900 truncate">{video?.file_name}</span>
                  File AVI/MP4 di disk, audio hasil ekstraksi, transkrip lengkap,
                  dan <b>{keyword_hits.length}</b> keyword hits akan dihapus permanen.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-sm" data-testid="detail-delete-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={deleting}
                  className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
                  data-testid="detail-delete-confirm"
                >
                  {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Ya, Hapus Permanen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </header>

      <div className="grid md:grid-cols-12 gap-5">
        <aside className="md:col-span-4 space-y-4">
          <div className="bg-slate-900 border border-slate-200 rounded-sm overflow-hidden" data-testid="video-player-container">
            {isBrowserPlayable(video?.file_name) ? (
              <video
                ref={videoRef}
                src={`${API_BASE}/videos/${video?.id}/stream`}
                controls
                preload="metadata"
                className="w-full aspect-video bg-black"
                data-testid="inline-video-player"
              />
            ) : (
              <div className="relative">
                <img src={VIDEO_PLACEHOLDER} alt="Broadcast" className="w-full aspect-video object-cover opacity-60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                  <Play className="h-8 w-8 text-white mb-2" />
                  <p className="text-xs text-white font-medium">Format {getEffectiveSuffix(video?.file_name).replace(".", "").toUpperCase()} tidak dapat diputar di browser.</p>
                  <a
                    href={`${API_BASE}/videos/${video?.id}/stream`}
                    className="mt-2 text-xs text-amber-200 underline"
                    data-testid="video-download-link"
                  >
                    Unduh file rekaman
                  </a>
                </div>
              </div>
            )}
            <div className="px-3 py-2 text-[11px] text-slate-300 border-t border-slate-800">
              Pemutar rekaman · Klik timestamp di kanan untuk lompat ke detik tertentu.
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-semibold flex items-center gap-2 mb-3">
              <Tag className="h-4 w-4 text-slate-400" /> Keyword Hits ({keyword_hits.length})
            </p>
            {keyword_hits.length === 0 ? (
              <p className="text-xs text-slate-500">Tidak ada keyword militer/pertahanan terdeteksi.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {keyword_hits.map((h) => (
                  <div key={h.id} className="text-sm flex items-center justify-between" data-testid={`hit-${h.keyword}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{h.keyword}</p>
                      <p className="text-[11px] uppercase tracking-wider text-slate-500">{h.category}</p>
                    </div>
                    <span className="font-mono text-xs bg-slate-900 text-white px-2 py-0.5 rounded-sm">
                      ×{h.occurrence_count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-slate-900 text-white rounded-sm p-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-300">Metadata</p>
            <dl className="mt-2 text-xs space-y-1">
              <div className="flex justify-between"><dt className="text-slate-400">Video ID</dt><dd className="font-mono">{video?.id?.slice(-12)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Source</dt><dd>{video?.ingest_source}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Bahasa</dt><dd>{transcript.language}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Segmen</dt><dd className="font-mono">{transcript.segments?.length || 0}</dd></div>
            </dl>
          </div>
        </aside>

        <section className="md:col-span-8 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <Tabs defaultValue="transcript" className="w-full">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <TabsList className="bg-slate-100 rounded-sm h-9 p-1" data-testid="detail-tabs">
                <TabsTrigger
                  value="transcript"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-xs uppercase tracking-wider font-semibold px-3"
                  data-testid="tab-transcript"
                >
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> Transkrip
                </TabsTrigger>
                <TabsTrigger
                  value="news"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-xs uppercase tracking-wider font-semibold px-3"
                  data-testid="tab-news"
                >
                  <Newspaper className="h-3.5 w-3.5 mr-1.5" /> Berita Terpisah
                  {newsItems.length > 0 && (
                    <span className="ml-1.5 bg-slate-900 text-white font-mono text-[10px] rounded-sm px-1.5 py-0.5">{newsItems.length}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="summary"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-xs uppercase tracking-wider font-semibold px-3"
                  data-testid="tab-summary"
                >
                  <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Ringkasan Intelijen
                  {summary && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-amber-500" />}
                </TabsTrigger>
              </TabsList>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Bahasa Indonesia · Whisper</p>
            </div>

            <TabsContent value="transcript" className="mt-0">
              {(() => {
                const segments = transcript.segments || [];
                const hasUsableSegments = segments.some(
                  (s) => (s.text || "").trim().length > 0 && (s.end > 0 || s.start > 0)
                );
                if (hasUsableSegments) {
                  return (
                    <div className="divide-y divide-slate-100 max-h-[calc(100vh-280px)] overflow-y-auto" data-testid="transcript-segments">
                      {segments.map((seg, idx) => (
                        <div
                          key={idx}
                          onClick={() => seekTo(seg.start)}
                          className={`grid grid-cols-[80px_1fr] gap-3 px-4 py-3 cursor-pointer transition-colors ${
                            activeSegment === idx ? "bg-amber-50 border-l-2 border-l-amber-500" : "hover:bg-slate-50"
                          }`}
                          data-testid={`segment-${idx}`}
                        >
                          <span className="font-mono text-xs text-slate-700 pt-0.5 hover:text-amber-700">{fmtTime(seg.start)}</span>
                          <p className="text-sm text-slate-800 leading-relaxed">
                            <Highlight text={seg.text} keywords={allKeywords} />
                          </p>
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div className="p-6 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="transcript-fulltext">
                    {transcript.full_text ? (
                      <Highlight text={transcript.full_text} keywords={allKeywords} />
                    ) : (
                      <span className="text-slate-500 italic">Transkrip belum tersedia atau kosong.</span>
                    )}
                  </div>
                );
              })()}
            </TabsContent>

            <TabsContent value="news" className="mt-0 max-h-[calc(100vh-280px)] overflow-y-auto" data-testid="news-panel">
              {newsItems.length === 0 ? (
                <div className="py-10 px-4 text-center" data-testid="news-empty">
                  <Newspaper className="h-10 w-10 text-slate-300 mx-auto" />
                  <p className="mt-3 text-sm font-semibold text-slate-800">Belum ada pemisahan berita</p>
                  <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                    Sistem akan otomatis memisahkan rekaman menjadi item berita individu saat transkripsi.
                    Untuk rekaman lama, klik tombol di bawah.
                  </p>
                  {(user?.role === "admin" || user?.role === "analis") && (
                    <Button
                      onClick={regenerateNews}
                      disabled={segmenting || !transcript.full_text}
                      className="mt-5 rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
                      data-testid="segment-news-button"
                    >
                      {segmenting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Menganalisis...</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" /> Pisahkan Berita Sekarang</>
                      )}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  <div className="px-4 py-2.5 bg-slate-50 flex items-center justify-between sticky top-0 z-10 border-b border-slate-200">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      <ListVideo className="inline h-3.5 w-3.5 mr-1" />
                      {newsItems.length} berita dipisahkan otomatis · klik untuk seek video
                    </p>
                    {(user?.role === "admin" || user?.role === "analis") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={regenerateNews}
                        disabled={segmenting}
                        className="rounded-sm border-slate-300 h-7 text-xs"
                        data-testid="news-regenerate-button"
                      >
                        {segmenting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        Re-segment
                      </Button>
                    )}
                  </div>
                  {newsItems.map((n) => {
                    const isActive = activeNewsId === n.id;
                    return (
                      <div
                        key={n.id}
                        onClick={() => seekTo(n.start_time)}
                        className={`px-4 py-3 cursor-pointer transition-colors ${
                          isActive ? "bg-amber-50 border-l-2 border-l-amber-500" : "hover:bg-slate-50"
                        }`}
                        data-testid={`news-item-${n.ordinal}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-sm bg-slate-900 text-white font-mono text-xs font-bold">
                            {n.ordinal}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm text-slate-900 leading-snug">{n.title}</p>
                            <p className="mt-1 text-xs text-slate-600 leading-relaxed">{n.summary}</p>
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                              <span className="font-mono text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">
                                {fmtTime(n.start_time)} — {fmtTime(n.end_time)}
                              </span>
                              {(n.matched_keywords || []).slice(0, 5).map((k) => (
                                <Badge
                                  key={k}
                                  className="rounded-sm bg-amber-100 text-amber-900 hover:bg-amber-200 border-none text-[10px] font-medium px-1.5 py-0"
                                >
                                  {k}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Play className={`h-4 w-4 shrink-0 mt-1 ${isActive ? "text-amber-600" : "text-slate-400"}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="summary" className="mt-0 p-4 max-h-[calc(100vh-280px)] overflow-y-auto" data-testid="summary-panel">
              {summary ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Dibuat: <span className="font-mono">{new Date(summary.generated_at).toLocaleString("id-ID")}</span> · Model: <span className="font-mono">{summary.model}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={downloadSummaryTxt}
                        className="rounded-sm border-slate-300 h-8 text-xs"
                        data-testid="summary-download-button"
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Unduh .txt
                      </Button>
                      {(user?.role === "admin" || user?.role === "analis") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={generateSummary}
                          disabled={generatingSummary}
                          className="rounded-sm border-slate-300 h-8 text-xs"
                          data-testid="summary-regenerate-button"
                        >
                          {generatingSummary ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Regenerate
                        </Button>
                      )}
                    </div>
                  </div>
                  <SummaryReport content={summary.content} />
                </div>
              ) : (
                <div className="py-10 text-center" data-testid="summary-empty">
                  <ShieldCheck className="h-10 w-10 text-slate-300 mx-auto" />
                  <p className="mt-3 text-sm font-semibold text-slate-800">Ringkasan intelijen belum dibuat</p>
                  <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                    AI akan menganalisis transkrip dan menghasilkan laporan terstruktur per kategori
                    (Institusi, Komando, Satuan Tempur, Operasi, Birokrasi, Perlengkapan, Frasa Umum).
                  </p>
                  {(user?.role === "admin" || user?.role === "analis") ? (
                    <Button
                      onClick={generateSummary}
                      disabled={generatingSummary || !transcript.full_text}
                      className="mt-5 rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
                      data-testid="summary-generate-button"
                    >
                      {generatingSummary ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Menganalisis transkrip...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Ringkasan Intelijen
                        </>
                      )}
                    </Button>
                  ) : (
                    <p className="mt-4 text-xs text-slate-400 italic">Hubungi admin/analis untuk membuat ringkasan.</p>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
}
